// src/components/Subscription/SubscriptionDrawer.js

import React, { useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Button,
  Text,
  Box,
  useToast,
  Divider,
  Spinner,
  Flex,
  IconButton,
  VStack,
  Heading,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import PaymentForm from '../PaymentForm';

// Inizializza Stripe.js
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const SubscriptionDrawer = ({ isOpen, onClose, tier, clientSecret }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleSuccess = async () => {
    setIsConfirming(true);
    try {
      toast({
        title: 'Abbonamento attivato!',
        status: 'success',
        isClosable: true,
      });
      onClose();
      navigate('/account/cilplus');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Errore conferma subscription:', err);
      toast({
        title: 'Errore nella conferma dell&apos;abbonamento.',
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Drawer
      placement="right"
      onClose={onClose}
      isOpen={isOpen}
      size="md"
      scrollBehavior="inside"
    >
      <DrawerOverlay bg="blackAlpha.600" />
      <DrawerContent bg="white" borderStartRadius="xl">
        <DrawerHeader
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={6}
          py={4}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Heading size="md">{tier.name}</Heading>
          <IconButton
            icon={<CloseIcon />}
            aria-label="Chiudi"
            variant="ghost"
            onClick={onClose}
            isDisabled={isConfirming}
          />
        </DrawerHeader>

        <DrawerBody px={6} py={4} overflowY="auto">
          <VStack spacing={4} align="stretch">
            <Box p={4} bg="gray.50" borderRadius="md" shadow="sm">
              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                Prezzo
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                {tier.price} €
                <Text as="span" fontSize="md" fontWeight="normal">
                  {' '}
                  / mese
                </Text>
              </Text>
            </Box>

            <Text fontStyle="italic" color="gray.600">
              Inserisci i dati di pagamento per completare l&apos;abbonamento.
            </Text>

            <Box
              p={4}
              bg="blue.50"
              borderRadius="md"
              border="1px solid"
              borderColor="blue.200"
            >
              <Text fontSize="sm" fontWeight="semibold" color="blue.800" mb={2}>
                💳 Pagamento Ricorrente Mensile
              </Text>
              <Text fontSize="sm" color="blue.700" lineHeight="1.6">
                Con questo abbonamento, verrà addebitato automaticamente{' '}
                {tier.price}€ ogni mese dalla tua carta. Potrai cancellare
                l&apos;abbonamento in qualsiasi momento dal tuo account, senza
                penali o costi aggiuntivi. Il pagamento ricorrente ti garantisce
                accesso continuo ai benefici del piano {tier.name} e sostiene le
                attività di CIL in modo costante.
              </Text>
            </Box>

            {!clientSecret ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <PaymentForm ambito="abbonamento" onSuccess={handleSuccess} />
              </Elements>
            )}
          </VStack>
        </DrawerBody>

        <Divider />

        <DrawerFooter px={6} py={4}>
          <Button
            variant="outline"
            w="full"
            onClick={onClose}
            isDisabled={isConfirming}
          >
            Annulla
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default SubscriptionDrawer;
